import { ComponentProps } from "react";
import { JsonSchema } from "@jsonforms/core";
import { JsonForms } from "@jsonforms/react";

/**
 * Taken from the prop rather than from ajv, which is not a dependency of ours: the shape is whatever
 * the JSON-Forms we have installed accepts.
 */
export type FormError = NonNullable<ComponentProps<typeof JsonForms>["additionalErrors"]>[number];

/**
 * The schema's required fields that are still empty, as the validation errors JSON-Forms merges
 * into what each control sees.
 *
 * JSON-Forms validates with ajv, which compiles every schema with `new Function`. The
 * Content-Security-Policy forbids that wherever it is enforced, so the form dies with an EvalError
 * before it renders. A form that runs with `validationMode="NoValidation"` never reaches ajv, and
 * applies the one rule its schema relies on through this instead.
 *
 * `required` is the only keyword applied. The send forms' schemas carry two others: `minLength: 1`,
 * which the integrations service emits only alongside `required` and which the empty-string check
 * below already covers, and `maxLength`, copied from the CRM's own field length. That one is no
 * longer refused before the send; an over-long value comes back as a named field on the CRM's
 * rejection instead.
 */
export function getRequiredFieldErrors(
  schema: JsonSchema,
  data: Record<string, unknown>,
): FormError[] {
  const required = Array.isArray(schema.required) ? schema.required : [];
  return required
    .filter((field) => {
      const value = data[field];
      return value === undefined || value === null || value === "";
    })
    .map((field) => ({
      instancePath: "",
      schemaPath: "#/required",
      keyword: "required",
      params: { missingProperty: field },
      // Mirrors ajv's own wording, which JSON-Forms' default translator turns into "is a required
      // property" — the message these forms showed before.
      message: `must have required property '${field}'`,
      parentSchema: schema,
    }));
}
